import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type Owner from '@ember/owner';
import { formatCurrency } from 'tio-ui/format';
import { PhCheck, PhClock, PhArchive, PhWarningCircle } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type { TOC } from '@ember/component/template-only';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import Button from 'tio-ui/components/button';
import Drawer, { DrawerBody } from 'tio-ui/components/drawer';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import ActivityView from 'tio-common/components/tas/course-cash/activity-view';
import ReceiptUploadModal from 'tio-employee/components/tas/course-cash/receipt-upload-modal';
import NeedsYourAttention from './needs-your-attention';
import DisbursementSection from './disbursement-section';
import ActionToolbar from './action-toolbar';
import ManageCommentsDrawer from './comments-drawer';
import ManageAttachmentsDrawer from './attachments-drawer';
import CardDetailsTab from './card-details-tab';
import TimelineDrawer from 'tio-common/components/tas/approval/drawers/timeline';
import type { ActionToolbarDrawerType } from './action-toolbar';
import DecisionNotice from './decision-notice';
import ShowCourseDrawer from 'tio-employee/components/tas/show-course-drawer';
import {
  employeeVisibleComments,
  unreadCommentCount,
  visibleCommentIds,
  commentsSeenStorageKey,
  parseSeenCommentIds,
} from 'tio-employee/utils/tas/comments';
import { deriveAttention } from 'tio-employee/utils/tas/attention-tasks';
import { deriveDisbursement } from 'tio-employee/utils/tas/disbursement';
import type { DisbursementCard } from 'tio-employee/utils/tas/disbursement';
import {
  fetchCourseCashTransactions,
  refreshCourseCashTransactions,
  uploadCourseCashReceipt,
  removeCourseCashReceipt,
} from 'tio-employee/utils/course-cash/receipt-actions';
import {
  saveCourseGrade,
  saveApplicationGpa,
  saveAgreementsForConditions,
  submitEvidenceForApproval,
} from 'tio-employee/utils/tas/evidence-actions';
import { loadTasApplicationHistory } from 'tio-data/requests/tas-application-actions';
import { PLACEHOLDER_MERCHANT_NAME } from 'tio-common/utils/course-cash/transaction-adapter';
import type { SubmitEvidenceBundle } from './task-card/submit-evidence';
import type { TasCourseExtension as TasCourse } from 'tio-data/data/resources/tas-course.ext';
import type {
  AttentionModel,
  AttentionTask,
  AttentionTaskKind,
} from 'tio-employee/utils/tas/attention-tasks';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { Store as StoreService } from '@warp-drive/core';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type SessionContextService from 'tio-employee/services/session-context';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';

// A receipt still needs attention until it's verified or explicitly not required.
const UNRESOLVED_RECEIPT_STATUSES = new Set(['needed', 'rejected', 'changes_needed']);

// Mirrors show.gts's local type of the same name.
type LockEmployeeStatusBasedOnType =
  | 'COURSE_START_DATE'
  | 'COURSE_END_DATE'
  | 'FULFILLED_DATE'
  | 'ON_COURSES_SUBMISSION'
  | 'ON_SUBMISSION';

/** Narrows a caught error to what ErrorMessages can render — a JSON:API-style
 * `{ errors: [...] }` document (the common `store.request` failure shape) or
 * a plain string message. */
function toDisplayError(error: unknown): string | ErrorsSignature {
  if (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as { errors: unknown }).errors)
  ) {
    return error as ErrorsSignature;
  }
  return error instanceof Error ? error.message : String(error);
}

interface ManagePageSignature {
  Args: {
    application: TasApplicationModel;
    /** From the route (see manage-route.ts) — undefined in contexts with no
     * route model (dev fixture gallery), where the eligibility lock simply
     * doesn't apply. */
    dynamicEligibility?: boolean;
    programEligibility?: string[];
  };
  Element: HTMLElement;
}

type RailIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

interface RailTaskRow {
  label: string;
  statusKey: string;
  icon: RailIcon;
  completed: boolean;
}

/** The rail's application-status line — see ManagePage#railStatus. */
interface RailStatusRow {
  /** Translation key for the label. */
  key: string;
  /** Value for `data-test-rail-status`. */
  test: string;
  /** Foreground token class. */
  tone: string;
  icon?: RailIcon;
}

/**
 * The application-status line under the Tasks rail.
 *
 * Extracted so it can render in both arms of the task-list conditional without
 * duplicating the markup: beside "None" when there is nothing to do, and as a
 * trailing line under the rows when there is.
 */
const RailStatus: TOC<{ Args: { status: RailStatusRow }; Element: HTMLSpanElement }> = <template>
  <span
    class="tio-row items-center gap-1 tio-text-xs-normal {{@status.tone}}"
    data-test-rail-status={{@status.test}}
    ...attributes
  >
    {{t @status.key}}
    {{#if @status.icon}}
      <@status.icon @size={{14}} />
    {{/if}}
  </span>
</template>;

const RAIL_TASK_LABEL_KEYS: Record<AttentionTaskKind, string> = {
  'use-course-cash': 'tuition_assistance.applications.manage.rail.task_use_course_cash',
  'upload-receipt': 'tuition_assistance.applications.manage.rail.task_upload_receipt',
  'submit-evidence': 'tuition_assistance.applications.manage.rail.task_submit_evidence',
};

// Amount rows are hidden until courses are pre-approved (see
// shouldDisplayApprovalAmounts).
const PRE_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

// Final approval has actually happened (see finalApprovedLabel).
const POST_FINAL_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

// Mirrors RejectionDetail's own state list (see isRejected) so the right-rail
// Tasks fallback doesn't contradict the RejectionDetail banner it sits next to.
const REJECTED_STATES = new Set([
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
]);

const RAIL_STATUS_KEYS: Record<AttentionTask['status'], string> = {
  pending: 'tuition_assistance.applications.manage.rail.status_pending',
  'in-progress': 'tuition_assistance.applications.manage.rail.status_in_progress',
  completed: 'tuition_assistance.applications.manage.rail.status_completed',
};

// The generic "pending" rail label reads as a state, not an action — for
// CourseCash specifically (PM feedback: unclear what to actually do), a
// pending use-course-cash task instead reads as an explicit call to action.
// Scoped to this one kind only; upload-receipt/submit-evidence keep the
// generic label until/unless the same feedback comes in for them too.
const RAIL_NEED_TO_USE_KEY = 'tuition_assistance.applications.manage.rail.status_need_to_use';

// TZ-safe MM/DD/YY. Date-only values (COURSES_BEGIN/END_DATE fields) are
// calendar days — read their parts directly (see attention-tasks chip note).
// Everything else (comment createdAt, transition-log timestamps) is an
// instant: parse it and read LOCAL calendar fields — slicing the string would
// bake in whatever offset it was serialized with, and the transition log mixes
// Z and legacy -05:00/-06:00 rows within one application.
function shortDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, year = '', month = '', day = ''] = dateOnly;
    return `${month}/${day}/${year.slice(2)}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${month}/${day}/${String(parsed.getFullYear()).slice(2)}`;
}

function divideBy100(value: number): number {
  return (value ?? 0) / 100;
}

// getSingleValueForTasField returns the broad FieldValue union; narrow to a
// string instead of casting, since a non-string field shape should not crash
// rendering or surface "[object Object]".
function fieldAsString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

// Same narrowing as fieldAsString, for number-typed template fields (e.g.
// POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME) — the JSONB-backed value
// can come back as a numeric string, so coerce rather than requiring `number`.
function fieldAsNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * The TAS course-management ("manage") screen: a clean header, the Needs Your
 * Attention task panel + request summary on the left, and a right rail (Tasks,
 * Request Details, Disbursement, Latest Comment). Reachable behind the
 * `tasCourseManagement` flag (see utils/tas/manage-route).
 */
export default class ManagePage extends Component<ManagePageSignature> {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked isCourseDrawerOpen = false;
  @tracked courseShowing: TasCourse | null = null;
  @tracked isActivityDrawerOpen = false;
  @tracked activeCardTab: 'details' | 'activity' = 'details';
  @tracked isCommentsDrawerOpen = false;
  @tracked isTimelineDrawerOpen = false;
  @tracked isAttachmentsDrawerOpen = false;
  @tracked courseCashTransactions: CourseCashTransaction[] = [];
  @tracked isReceiptModalOpen = false;
  @tracked acceptedEvidenceConditions: ConditionModel[] = [];
  @tracked evidenceSubmitError: string | ErrorsSignature | null = null;
  @tracked selectedReceiptTransaction: CourseCashTransaction | null = null;

  /** Comment ids that were on screen the last time this employee opened the
   * comments drawer, mirrored from localStorage. Tracked because localStorage is
   * not reactive — without the mirror the badge would not clear until a reload. */
  @tracked commentsSeenIds: ReadonlySet<string> | null = null;

  constructor(owner: Owner, args: ManagePageSignature['Args']) {
    super(owner, args);
    this.commentsSeenIds = this.#readCommentsSeenIds();
    this.loadCourseCashTransactions();
    this.loadApplicationHistory();
  }

  /** Null for an application with no server identity yet — there is nothing
   * stable to key read-state against, so it simply isn't persisted. */
  get #commentsStorageKey(): string | null {
    const id = this.args.application.id;
    return id ? commentsSeenStorageKey(id) : null;
  }

  /** localStorage can throw (Safari private browsing, storage disabled), and an
   * unreadable value should degrade to "everything unread" rather than break the
   * page. */
  #readCommentsSeenIds(): ReadonlySet<string> | null {
    const key = this.#commentsStorageKey;
    if (!key) {
      return null;
    }
    try {
      return parseSeenCommentIds(localStorage.getItem(key));
    } catch {
      return null;
    }
  }

  get application(): TasApplicationModel {
    return this.args.application;
  }

  /** Load Course Cash transactions from the approvables endpoint + assistance data. */
  loadCourseCashTransactions = async () => {
    this.courseCashTransactions = await fetchCourseCashTransactions(this.store, this.application);
  };

  /**
   * Loads application.history (state transitions + assistance/payment records)
   * — NOT part of this route's own findRecord include. Feeds the Timeline
   * drawer's `fullHistory` (`application.history` ++ `paymentsHistory`).
   * Loaded eagerly here, not lazily on Timeline-drawer-open, so the drawer is
   * correct from the first open. Best-effort: unlike loadCourseCashTransactions
   * (a no-op network call when there are no assistances yet), this always
   * issues a real request, so a failure — dev/test environments with no
   * history endpoint mocked, a brand-new application with no history yet —
   * must not crash the page.
   *
   * The Disbursement rail (`disbursementCards`) does NOT depend on this — it
   * reads `tasAssistances` directly, which IS part of the route's findRecord
   * include (see manage-route.ts), so it renders correctly before this
   * resolves.
   */
  loadApplicationHistory = async () => {
    try {
      await loadTasApplicationHistory(this.store, this.application);
    } catch (error) {
      console.error('Failed to load application history:', error);
    }
  };

  reloadCourseCashTransactions = async () => {
    this.courseCashTransactions = await refreshCourseCashTransactions(this.store, this.application);
  };

  /**
   * Every live transaction still needing a receipt — one upload-receipt task
   * per swipe (Figma). Optional-chained: manage-page-attention-model-test.ts
   * exercises `attentionModel` on a class instance built via `Object.create`
   * (no constructor run, so `courseCashTransactions` was never initialized).
   */
  get unresolvedReceiptTransactions(): CourseCashTransaction[] {
    return (this.courseCashTransactions ?? []).filter((txn) =>
      UNRESOLVED_RECEIPT_STATUSES.has(txn.receiptStatus)
    );
  }

  get hasLiveCourseCashData(): boolean {
    return (this.application.tasAssistances?.length ?? 0) > 0;
  }

  /**
   * Single-task fallback for contexts with no live assistance data (dev
   * fixture gallery, unit tests — see the TODO atop attention-tasks.ts; the
   * real TasApplicationModel has no receiptUploaded field of its own). Once
   * there IS live data, `attentionModel` uses `receiptTasks` instead and
   * ignores this.
   */
  get receiptUploadedFallback(): boolean {
    const fixtureFlag = (this.application as unknown as { receiptUploaded?: boolean })
      .receiptUploaded;
    return Boolean(fixtureFlag);
  }

  openReceiptModal = (transaction: CourseCashTransaction) => {
    this.selectedReceiptTransaction = transaction;
    this.isReceiptModalOpen = true;
  };

  /**
   * @onUploadReceipt for NeedsYourAttention — each upload-receipt task's id is
   * `upload-receipt-<transactionId>` (see attention-tasks.ts), so strip the
   * prefix to find the transaction this specific card's button was clicked for.
   */
  openReceiptModalForTask = (task: AttentionTask) => {
    const transactionId = task.id.replace(/^upload-receipt-/, '');
    const transaction = this.courseCashTransactions.find((t) => t.id === transactionId);
    if (transaction) {
      this.openReceiptModal(transaction);
    }
  };

  closeReceiptModal = () => {
    this.isReceiptModalOpen = false;
    this.selectedReceiptTransaction = null;
  };

  handleReceiptUpload = async (
    files: DigitalAssetDetailSignature[],
    transaction: CourseCashTransaction
  ) => {
    await uploadCourseCashReceipt(
      this.store,
      this.digitalAssets,
      this.application,
      files,
      transaction
    );
    await this.reloadCourseCashTransactions();
  };

  handleRemoveReceipt = async (receipt: CourseCashReceipt, transaction: CourseCashTransaction) => {
    await removeCourseCashReceipt(this.store, receipt);
    await this.reloadCourseCashTransactions();

    const updatedTransaction = this.courseCashTransactions.find((t) => t.id === transaction.id);
    if (updatedTransaction) {
      this.selectedReceiptTransaction = updatedTransaction;
    }
  };

  /**
   * Evidence submission — mirrors the legacy show page's grade modal +
   * attachments section + AcceptConditions + submitForEvidenceApproval (see
   * `tio-employee/app/utils/tas/evidence-actions.ts`), wired inline instead of
   * routing the "Submit Evidence" task out to the guided wizard.
   */

  get isActiveParticipant(): boolean {
    return this.application.tasProgramInstance?.employee?.tasParticipant?.isActive ?? false;
  }

  get conditionsForEvidence(): readonly ConditionModel[] {
    return (
      this.application.tasProgramInstance?.tasProgramTemplate
        ?.applicationApproveEvidenceConditions ?? []
    );
  }

  get hasAgreedAllEvidenceConditions(): boolean {
    if (!this.conditionsForEvidence.length) {
      return true;
    }
    return this.conditionsForEvidence.length === this.acceptedEvidenceConditions.length;
  }

  /** Mirrors the legacy page's showGPAField — only Type 3/4.3 templates configured to calculate the total by GPA. */
  get showGPAField(): boolean {
    const typeClassification = this.application.typeClassification;
    return Boolean(
      this.application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA &&
      (typeClassification === 'TAS.ProgramType.3' || typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get hasGpaIfRequired(): boolean {
    if (!this.showGPAField) {
      return true;
    }
    return Boolean(getSingleValueForTasField('GRADE_POINT_AVERAGE', this.application.fields));
  }

  /** Mirrors the legacy page's lockStatus. */
  get lockStatus(): string {
    const lockType: Record<LockEmployeeStatusBasedOnType, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    const lockEmployeeStatusBasedOn = this.application.tasProgramInstance?.tasProgramTemplate
      ?.lockEmployeeStatusBasedOn as LockEmployeeStatusBasedOnType | undefined;
    return lockEmployeeStatusBasedOn && lockEmployeeStatusBasedOn in lockType
      ? lockType[lockEmployeeStatusBasedOn]
      : '';
  }

  /** Mirrors the legacy page's startOrEndDate. */
  get startOrEndDate(): unknown {
    if (this.lockStatus === 'start') {
      return this.application.coursesBeginDate;
    }
    if (this.lockStatus === 'end') {
      return fieldAsString(getSingleValueForTasField('COURSES_END_DATE', this.application.fields));
    }
    return new Date().toLocaleDateString('en-CA');
  }

  /** Mirrors the legacy page's calculateEligibility. */
  calculateEligibility(): boolean {
    const programEligibility = this.args.programEligibility ?? [];
    if (!programEligibility.length) {
      return false;
    }
    const dateValue = this.startOrEndDate;
    if (!dateValue || typeof dateValue !== 'string') {
      return false;
    }
    const enteredDate = new Date(dateValue);
    return programEligibility.some((period) => {
      const [start, end] = period as unknown as [string, string];
      return enteredDate >= new Date(start) && enteredDate <= new Date(end);
    });
  }

  /**
   * Mirrors the legacy page's inEligible — the policy-lock check driven by
   * dynamic eligibility + eligibility periods (both fetched by
   * manage-route.ts, the same way the legacy show route fetches them).
   */
  get inEligible(): boolean {
    const app = this.application;
    if (app.ignorePolicies || !this.lockStatus) {
      return false;
    }
    const completedStates = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (completedStates.includes(app.state)) {
      return false;
    }
    if (['fulfilled', 'submission'].includes(this.lockStatus)) {
      return false;
    }
    return !(this.args.dynamicEligibility && this.calculateEligibility());
  }

  /**
   * Mirrors the legacy page's canSubmitApplication.
   */
  get canSubmitEvidence(): boolean {
    const app = this.application;
    if (app.ignorePolicies) {
      return true;
    }
    const sharedConditions =
      this.isActiveParticipant &&
      this.hasAgreedAllEvidenceConditions &&
      app.hasRequiredEvidenceAttachments &&
      !this.inEligible &&
      app.state === 'TAS.ApplicationState.ATTEND' &&
      this.hasGpaIfRequired;
    return app.isCourseGradeRequired
      ? sharedConditions && !app.isMissingCourseGrades
      : sharedConditions;
  }

  get evidenceSubmissionBundle(): SubmitEvidenceBundle {
    return {
      onSaveGrade: this.handleSaveCourseGrade,
      onSaveGpa: this.handleSaveGpa,
      onAcceptConditions: this.handleAcceptEvidenceConditions,
      onSubmit: () => this.submitEvidence.perform(),
      isSubmitting: this.submitEvidence.isRunning,
      submitError: this.evidenceSubmitError,
      canSubmit: this.canSubmitEvidence,
    };
  }

  handleSaveCourseGrade = async (course: TasCourse, grade: string) => {
    await saveCourseGrade(this.store, course, grade);
  };

  handleSaveGpa = async (gpa: number) => {
    await saveApplicationGpa(this.store, this.tuitionAssistance, this.application, gpa);
  };

  handleAcceptEvidenceConditions = (conditions: ConditionModel[]) => {
    this.acceptedEvidenceConditions = conditions;
  };

  submitEvidence = dropTask(async () => {
    this.evidenceSubmitError = null;
    try {
      await saveAgreementsForConditions(
        this.store,
        this.sessionContext.user,
        this.acceptedEvidenceConditions
      );
      await submitEvidenceForApproval(this.store, this.sessionContext, this.application);
    } catch (error) {
      console.error('Failed to submit evidence:', error);
      this.evidenceSubmitError = toDisplayError(error);
    }
  });

  get pcsCards() {
    return this.application.pcsCards ?? [];
  }

  get pcsCardsWithMeta() {
    const cards = this.pcsCards;
    return cards.map((card, i) => ({ card, cardNumber: i + 1, totalCards: cards.length }));
  }

  openActivityDrawer = () => {
    this.isActivityDrawerOpen = true;
    this.activeCardTab = 'details';
  };

  closeActivityDrawer = () => {
    this.isActivityDrawerOpen = false;
  };

  switchCardTab = (tab: 'details' | 'activity') => {
    this.activeCardTab = tab;
  };

  // Maps each card-drawer tab button's element by tab name (populated by the
  // registerCardTabButton modifier) so handleCardTabKeydown can focus the
  // other tab without a global document query — a global query can hit a
  // different ManagePage instance's tab button when the manage gallery
  // renders many instances at once.
  #cardTabButtons = new Map<'details' | 'activity', HTMLElement>();

  registerCardTabButton = modifier((element: HTMLElement, [tab]: ['details' | 'activity']) => {
    this.#cardTabButtons.set(tab, element);
    return () => {
      this.#cardTabButtons.delete(tab);
    };
  });

  /**
   * Standard left/right roving-tabindex behavior for the two-tab card
   * drawer. Attached to each tab button individually (not the tablist
   * container) — a keydown listener on a plain, non-interactive `<div>`
   * fails the `no-invalid-interactive` template lint rule.
   */
  handleCardTabKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const next = this.activeCardTab === 'details' ? 'activity' : 'details';
    this.switchCardTab(next);
    this.#cardTabButtons.get(next)?.focus();
  };

  openCommentsDrawer = () => {
    this.isCommentsDrawerOpen = true;
    this.#markCommentsViewed();
  };

  /** Marking on OPEN, not close: opening is the point at which the employee has
   * been shown the comments, and it keeps the badge from lingering behind an open
   * drawer.
   *
   * Records the ids on screen rather than a timestamp — see unreadCommentCount for
   * why (a persisted timestamp would come from the viewer's clock while `createdAt`
   * comes from the author's). */
  #markCommentsViewed() {
    const ids = visibleCommentIds(this.application.comments);
    this.commentsSeenIds = new Set(ids);
    const key = this.#commentsStorageKey;
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(ids));
    } catch {
      // Storage unavailable — the tracked value still clears the badge for this
      // session; it just won't survive a reload.
    }
  }

  closeCommentsDrawer = () => {
    this.isCommentsDrawerOpen = false;
  };

  openTimelineDrawer = () => {
    this.isTimelineDrawerOpen = true;
  };

  closeTimelineDrawer = () => {
    this.isTimelineDrawerOpen = false;
  };

  openAttachmentsDrawer = () => {
    this.isAttachmentsDrawerOpen = true;
  };

  closeAttachmentsDrawer = () => {
    this.isAttachmentsDrawerOpen = false;
  };

  /**
   * Every asset actually attached to this application, deduplicated —
   * mirrors approval-page.gts's own `allAttachments` aggregation: assets can
   * be linked at the program-instance, course, application, or assistance
   * (payment) level, and the same asset can legitimately appear via more
   * than one of those relationships.
   */
  get allAttachments(): TasAssetModel[] {
    const programInstanceAssets = this.application.tasProgramInstance?.tasAssets ?? [];
    const courseAssets =
      this.application.activeCourses?.flatMap((course) => course.tasAssets ?? []) ?? [];
    const appAssets = this.application.tasAssets ?? [];
    const assistanceAssets =
      this.application.tasAssistances?.flatMap((assistance) => assistance.tasAssets ?? []) ?? [];

    const seenIds = new Set<string>();
    const allAssets = [
      ...programInstanceAssets,
      ...courseAssets,
      ...appAssets,
      ...assistanceAssets,
    ];
    return allAssets.filter((asset) => {
      const id = asset.id;
      if (!id || seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
  }

  get activeToolbarDrawer(): ActionToolbarDrawerType | null {
    if (this.isActivityDrawerOpen) {
      return 'course-cash';
    }
    if (this.isCommentsDrawerOpen) {
      return 'comments';
    }
    if (this.isTimelineDrawerOpen) {
      return 'timeline';
    }
    if (this.isAttachmentsDrawerOpen) {
      return 'attachments';
    }
    return null;
  }

  /**
   * Dispatches the bottom ActionToolbar's icon clicks — every drawer now
   * reuses an established component (`course-cash`/`timeline`/`comments`/
   * `attachments`); none are stubs anymore.
   */
  handleToolbarDrawerOpen = (drawerType: ActionToolbarDrawerType) => {
    switch (drawerType) {
      case 'course-cash':
        this.openActivityDrawer();
        break;
      case 'attachments':
        this.openAttachmentsDrawer();
        break;
      case 'timeline':
        this.openTimelineDrawer();
        break;
      case 'comments':
        this.openCommentsDrawer();
        break;
    }
  };

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  openCourseDrawer = (course: TasCourse) => {
    this.courseShowing = course;
    this.isCourseDrawerOpen = true;
  };

  closeCourseDrawer = () => {
    this.isCourseDrawerOpen = false;
    this.courseShowing = null;
  };

  get courseDrawerHeader(): string {
    return this.courseShowing?.displayName ?? '';
  }

  /** Raw `COURSES_END_DATE` (YYYY-MM-DD) — feeds submit-evidence's due-date
   * math in deriveAttention. Distinct from datesLabel/startOrEndDate above,
   * which format this same field for display rather than date arithmetic. */
  get courseEndDate(): string | undefined {
    return fieldAsString(getSingleValueForTasField('COURSES_END_DATE', this.application.fields));
  }

  /** Per-program-template admin setting (tio-api seed default: 45 days) —
   * courseEndDate + this = the same deadline TasApplicationAutoRejectionJob
   * enforces (see attention-tasks.ts's deriveEvidenceDueDate). */
  get evidenceMaxLagDays(): number | undefined {
    return fieldAsNumber(
      getSingleValueForTasField(
        'POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME',
        this.programTemplate?.fields
      )
    );
  }

  get attentionModel(): AttentionModel {
    const app = this.application;
    return deriveAttention({
      state: app.state,
      amounts: app.amounts as Parameters<typeof deriveAttention>[0]['amounts'],
      isEvidenceRequired: app.isEvidenceRequired,
      isMissingRequiredEvidence: app.isMissingRequiredEvidence,
      isCourseGradeRequired: app.isCourseGradeRequired,
      isMissingCourseGrades: app.isMissingCourseGrades,
      isApproved: app.isApproved,
      courseEndDate: this.courseEndDate,
      evidenceMaxLagDays: this.evidenceMaxLagDays,
      // Read the model getter the legacy show page badges from, rather than
      // re-deriving the refund arithmetic here — the two screens must agree.
      inRepayment: app.inRepayment,
      outstandingRepayment: this.outstandingRepayment,
      courseCashUseByDate: this.courseCashUseByDate,
      // Same state-gated total the rail uses (elevator-pitch#210): the raw
      // coalescing approvedTotal (FINAL ?? PRE) reads as a real final number
      // before final approval has actually happened, which is exactly the
      // "Your approval total is $0.00" confusion the banner needs to avoid.
      approvedTotal: this.hasPassedFinalApproval
        ? (app.finalApprovedTotal ?? 0)
        : (app.preApprovedTotal ?? 0),
      receiptTasks: this.hasLiveCourseCashData
        ? this.unresolvedReceiptTransactions.map((txn) => ({
            id: txn.id,
            dueDate: txn.receiptDueDate ?? null,
            status: txn.receiptStatus,
            amount: txn.amount,
            transactionDate: txn.transactionDate,
            // The adapter coalesces down to a generic placeholder; the receipt row would
            // rather show the school (or nothing) than the word "Merchant".
            merchantName: txn.merchantName === PLACEHOLDER_MERCHANT_NAME ? null : txn.merchantName,
          }))
        : undefined,
      receiptUploaded: this.receiptUploadedFallback,
    });
  }

  get programName(): string {
    return this.application.tasProgramInstance?.tasProgramTemplate?.programName ?? '';
  }

  get institutionName(): string {
    return (
      fieldAsString(
        getSingleValueForTasField(
          'SCHOOL_INSTITUTION_NAME',
          this.application.tasProgramInstance?.fields
        )
      ) ?? ''
    );
  }

  get submittedLabel(): string {
    const date = shortDate(this.application.applicationSubmittedDate);
    return date
      ? this.intl.t('tuition_assistance.applications.manage.header.submitted_on', { date })
      : '';
  }

  get isDirectPayProgram(): boolean {
    return this.application.tasProgramInstance?.tasProgramTemplate?.isDirectPayProgram ?? false;
  }

  get datesLabel(): string {
    const start = shortDate(this.application.coursesBeginDate);
    const end = shortDate(
      fieldAsString(getSingleValueForTasField('COURSES_END_DATE', this.application.fields))
    );
    return start && end ? `${start}–${end}` : start || end;
  }

  get requestedLabel(): string {
    return formatCurrency((this.application.requestedTotal ?? 0) / 100);
  }

  get preApprovedLabel(): string {
    return formatCurrency((this.application.preApprovedTotal ?? 0) / 100);
  }

  /**
   * The backend mirrors PRE_APPROVED_TOTAL into FINAL_APPROVED_TOTAL on every
   * save until final approval overwrites it (TasApplication
   * #sync_approved_totals_if_applicable), so the raw field cannot distinguish
   * "not finally approved yet" — only the state can. Read $0.00 until the
   * application has actually passed final (evidence) approval.
   */
  get finalApprovedLabel(): string {
    if (!this.hasPassedFinalApproval) {
      return formatCurrency(0);
    }
    return formatCurrency((this.application.finalApprovedTotal ?? 0) / 100);
  }

  get hasPassedFinalApproval(): boolean {
    return POST_FINAL_APPROVAL_STATES.has(this.application.state);
  }

  get isRejected(): boolean {
    return REJECTED_STATES.has(this.application.state);
  }

  get isAbandoned(): boolean {
    return this.application.state === 'TAS.ApplicationState.ABANDONED';
  }

  /**
   * The request has run its full course — paid out and closed. The empty
   * Tasks rail otherwise falls through to "application in review", which
   * reads as still-pending on a request that finished.
   *
   * Gated on `isInRepayment`: a FULFILLED request can still owe money back
   * (state and payment state are independent), and a green "application
   * complete" over an outstanding balance is worse than the wrong label it
   * replaced. The rail reports the repayment instead — see the template.
   */
  get isFulfilled(): boolean {
    return this.application.state === 'TAS.ApplicationState.FULFILLED' && !this.isInRepayment;
  }

  /** Mirrors the legacy show page's "In Repayment" badge (`inRepayment`). */
  get isInRepayment(): boolean {
    return this.application.inRepayment ?? false;
  }

  /** Outstanding repayment balance in cents. */
  get outstandingRepayment(): number {
    const amounts = this.application.amounts as
      | {
          refund_requested?: number;
          refund_paid_back?: number;
          refund_forgiven?: number;
          refund_cancelled?: number;
        }
      | undefined;
    const requested = amounts?.refund_requested ?? 0;
    const settled =
      (amounts?.refund_paid_back ?? 0) +
      (amounts?.refund_forgiven ?? 0) +
      (amounts?.refund_cancelled ?? 0);
    return Math.max(0, requested - settled);
  }

  /**
   * The approval amount rows appear once courses preapproval has happened:
   * hidden in CREATED and every pre-approval state, visible from
   * COURSES_APPROVED onward. This deliberately diverges from the model's
   * shouldDisplayApprovedTotal (which waits for ATTEND and doesn't exclude
   * CREATED) because the preapproved amount is exactly what the employee
   * wants to see right after preapproval (elevator-pitch#210).
   */
  get shouldDisplayApprovalAmounts(): boolean {
    return !PRE_APPROVAL_STATES.has(this.application.state);
  }

  /** The active PCS card, falling back to the first — same selection needs-your-attention.gts uses for cardLast4. */
  get primaryPcsCard() {
    const cards = this.pcsCards;
    return cards.find((card) => card.isActive) ?? cards[0];
  }

  /** The live CourseCash reserve carries the rolling funds-expiry date; captured/
   * consumed siblings return null — same selection card-details-tab.gts's
   * courseCashReserve getter uses. Raw YYYY-MM-DD string; DisbursementSection
   * formats it for display. */
  get courseCashUseByDate(): string | null {
    return (
      this.application.tasAssistances?.find(
        (assistance) => assistance.isCourseCash && assistance.courseCashExpiresAt
      )?.courseCashExpiresAt ?? null
    );
  }

  get disbursementCards(): DisbursementCard[] {
    const app = this.application;
    const amounts = app.amounts as
      { course_cash?: { reserved?: number; used?: number } } | undefined;

    return deriveDisbursement({
      state: app.state,
      typeClassification: app.typeClassification,
      isPrepaidProgram: app.isPrepaidProgram,
      isDirectPayProgram: this.isDirectPayProgram,
      requestedTotal: app.requestedTotal ?? 0,
      requestedCourseCashSubtotal: app.requestedCourseCashSubtotal ?? 0,
      requestedNonCourseCashSubtotal: app.requestedNonCourseCashSubtotal ?? 0,
      preApprovedTotal: app.preApprovedTotal ?? 0,
      finalApprovedTotal: app.finalApprovedTotal ?? 0,
      paidTotal: app.paidTotal ?? 0,
      courseCashReserved: amounts?.course_cash?.reserved ?? 0,
      courseCashUsed: amounts?.course_cash?.used ?? 0,
      courseCashUseByDate: this.courseCashUseByDate,
      pcsCardDeactivated: this.primaryPcsCard?.isDeactivated ?? false,
    });
  }

  // Render the rail task label with the CourseCa$h™ brand mark styled. The
  // "use course cash" task is brand-only, so it renders the canonical mark
  // directly; "upload receipt" is brand + " receipt", so the mark is injected
  // into the localized string as a SafeString. Both emit HTML (htmlSafe).
  railTaskLabel(kind: AttentionTaskKind): string {
    const courseCash = this.intl.t('common.tas.course_cash.default', { htmlSafe: true });
    if (kind === 'use-course-cash') {
      return courseCash;
    }
    if (kind === 'upload-receipt') {
      return this.intl.t(RAIL_TASK_LABEL_KEYS[kind], { htmlSafe: true, courseCash });
    }
    return this.intl.t(RAIL_TASK_LABEL_KEYS[kind]);
  }

  railStatusKeyFor(task: AttentionTask): string {
    if (task.kind === 'use-course-cash' && task.status === 'pending') {
      return RAIL_NEED_TO_USE_KEY;
    }
    return RAIL_STATUS_KEYS[task.status];
  }

  get railTasks(): RailTaskRow[] {
    return this.attentionModel.tasks.map((task) => ({
      label: this.railTaskLabel(task.kind),
      statusKey: this.railStatusKeyFor(task),
      icon: task.status === 'completed' ? PhCheck : PhClock,
      completed: task.status === 'completed',
    }));
  }

  /**
   * The application's own status line for the Tasks rail, independent of
   * whether the rail currently lists any tasks.
   *
   * It used to live only in the `{{else}}` arm of the task-list conditional, so
   * a single row in `railTasks` hid it entirely — and a FULFILLED CourseCa$h
   * request always has one, because `deriveAttention` keeps the spent
   * `use-course-cash` task in the rail (completed, filtered out of the NYA
   * cards). The result: the legacy show page said "Application is now
   * complete" while this rail said nothing. Same coupling that hid the
   * "application withdrawn" label on ABANDONED requests, which was worked
   * around there by emptying the tasks array instead; the status of a request
   * and the list of its tasks are different facts, so neither should hide the
   * other.
   *
   * Ordering matches the priority the anchors use: an outstanding balance
   * outranks everything else on a closed request, then withdrawal, then
   * completion, then rejection.
   *
   * `in_review` is the one status still gated on an empty list — it is the
   * filler for "nothing to do, we are looking at it", which would read as
   * nonsense printed next to tasks the employee still owes us.
   */
  get railStatus(): RailStatusRow | null {
    if (this.isInRepayment) {
      return {
        key: 'tuition_assistance.applications.manage.rail.in_repayment',
        test: 'in-repayment',
        tone: 'tio-fg-warning',
        icon: PhWarningCircle,
      };
    }
    if (this.isAbandoned) {
      return {
        key: 'tuition_assistance.applications.manage.rail.abandoned',
        test: 'abandoned',
        tone: 'tio-fg-danger',
        icon: PhArchive,
      };
    }
    if (this.isFulfilled) {
      return {
        key: 'tuition_assistance.applications.manage.rail.complete',
        test: 'complete',
        tone: 'tio-fg-success',
        icon: PhCheck,
      };
    }
    if (this.isRejected) {
      return {
        key: 'tuition_assistance.applications.manage.rail.rejected',
        test: 'rejected',
        tone: 'tio-fg-secondary',
      };
    }
    if (!this.railTasks.length) {
      return {
        key: 'tuition_assistance.applications.manage.rail.in_review',
        test: 'in-review',
        tone: 'tio-fg-secondary',
      };
    }
    return null;
  }

  get courses() {
    return this.application.activeCourses ?? [];
  }

  /**
   * Newest comment visible to the employee — must match the same
   * employee-visible tag filter, `isNew` exclusion, and createdAt-descending
   * selection as the Comments drawer (ManageCommentsDrawer / content.gts's
   * `employeeComments`), so an internal approver-only note never surfaces
   * here, and a comment that's still saving (or failed to save) never shows
   * as the "latest" one.
   */
  get latestComment(): { text: string; date: string } | null {
    const comment = employeeVisibleComments(this.application.comments)[0];
    if (!comment) {
      return null;
    }
    return { text: comment.comment ?? '', date: shortDate(comment.createdAt) };
  }

  /** Comments from someone else that the employee has not seen — drives the
   * toolbar's unread badge. Their own comments are excluded: they know what they
   * just wrote, and counting them would badge every reply back at its author. */
  get unreadCommentCount(): number {
    return unreadCommentCount(
      this.application.comments,
      this.commentsSeenIds,
      this.sessionContext.person?.id
    );
  }

  <template>
    <section
      class="tio-bg-surface tio-col gap-6 rounded-2xl border tio-border-default p-6 overflow-hidden"
      ...attributes
    >
      {{! Header }}
      <header class="tio-row-between items-start gap-4 border-b tio-border-default pb-4">
        <div class="min-w-0">
          <div class="tio-row flex-wrap gap-2">
            <h1 class="tio-text-xl-semibold tio-fg-primary">{{this.application.displayName}}</h1>
            <CourseCashTag @isDirectPayProgram={{this.isDirectPayProgram}} @size="sm" />
          </div>
          <p class="tio-text-sm-normal tio-fg-secondary mt-1">
            {{#if this.programName}}{{this.programName}}{{/if}}
            {{#if this.institutionName}}• {{this.institutionName}}{{/if}}
            {{#if this.submittedLabel}}• {{this.submittedLabel}}{{/if}}
          </p>
        </div>
      </header>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {{! Main column }}
        <div class="tio-col gap-6 lg:col-span-2">
          {{! Self-gates: renders only for COURSES_NOT_APPROVED/COURSES_REJECTED/
            EVIDENCE_NOT_APPROVED/EVIDENCE_REJECTED. This screen used to borrow the
            reimagined show page's RejectionDetail, which carries a reviewer-note
            block, a tip banner and a resubmit CTA — none of which the Figma
            "Decision Notification" spec has. DecisionNotice is that spec. }}
          <DecisionNotice @application={{this.application}} />

          <NeedsYourAttention
            @model={{this.attentionModel}}
            @application={{this.application}}
            @onSubmitEvidence={{this.evidenceSubmissionBundle}}
            @onViewActivity={{this.openActivityDrawer}}
            @onUploadReceipt={{this.openReceiptModalForTask}}
          />

          {{! Request Summary }}
          <section class="tio-bg-surface rounded-2xl border tio-border-default p-5">
            <div class="tio-row-between items-baseline">
              <h2 class="tio-text-lg-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.summary.heading"}}
              </h2>
              <span class="tio-text-sm-normal tio-fg-secondary">
                {{t
                  "tuition_assistance.applications.manage.summary.items"
                  count=this.courses.length
                }}
              </span>
            </div>
            <ul class="tio-col mt-3 gap-2">
              {{#each this.courses as |course|}}
                <li class="tio-row-between gap-3 border-t tio-border-default pt-2">
                  <Button
                    @appearance="minimal"
                    data-test-manage-course-name
                    class="tio-text-sm-medium tio-fg-info underline truncate min-w-0 justify-start p-0"
                    {{on "click" (fn this.openCourseDrawer course)}}
                  >
                    {{course.displayName}}
                  </Button>
                  <span class="tio-text-sm-normal tio-fg-primary shrink-0">
                    {{formatCurrency (divideBy100 course.courseTuition)}}
                  </span>
                </li>
              {{/each}}
            </ul>
          </section>
        </div>

        {{! Right rail — one unified "Progress Indicator" card: Tasks · Request
            Details · Disbursement, separated by dividers (per the design). }}
        <aside class="tio-col gap-5">
          <section class="tio-bg-surface tio-col gap-5 rounded-2xl border tio-border-default p-5">
            {{! Tasks }}
            <div>
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.tasks_heading"}}
                ({{this.attentionModel.pendingCount}})
              </h2>
              {{#if this.railTasks.length}}
                <dl class="tio-col mt-3 gap-2">
                  {{#each this.railTasks as |row|}}
                    <div class="tio-row-between gap-2">
                      <dt class="tio-text-sm-normal tio-fg-primary">{{row.label}}</dt>
                      <dd
                        class="tio-row gap-1 {{if row.completed 'tio-fg-info' 'tio-fg-secondary'}}"
                      >
                        <span class="tio-text-xs-normal">{{t row.statusKey}}</span>
                        <row.icon @size={{14}} />
                      </dd>
                    </div>
                  {{/each}}
                </dl>
                {{! The status line renders under the rows rather than beside
                    them — it describes the request, not any one task. }}
                {{#if this.railStatus}}
                  <div class="tio-row mt-2 justify-end">
                    <RailStatus @status={{this.railStatus}} />
                  </div>
                {{/if}}
              {{else}}
                <div class="tio-row-between mt-3 gap-2">
                  <span class="tio-text-sm-normal tio-fg-primary">
                    {{t "tuition_assistance.applications.manage.rail.none"}}
                  </span>
                  {{#if this.railStatus}}
                    <RailStatus @status={{this.railStatus}} />
                  {{/if}}
                </div>
              {{/if}}
            </div>

            <div class="border-t tio-border-default"></div>

            {{! Request Details }}
            <div>
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.request_details"}}
              </h2>
              <dl class="tio-col mt-3 gap-1">
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.dates"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.datesLabel}}</dd>
                </div>
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.expense_items"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.courses.length}}</dd>
                </div>
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.requested"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.requestedLabel}}</dd>
                </div>
                {{#if this.shouldDisplayApprovalAmounts}}
                  <div class="tio-row-between gap-2">
                    <dt class="tio-text-sm-normal tio-fg-secondary">
                      {{t "tuition_assistance.applications.manage.rail.pre_approved"}}
                    </dt>
                    <dd class="tio-text-sm-normal tio-fg-primary">{{this.preApprovedLabel}}</dd>
                  </div>
                  <div class="tio-row-between gap-2">
                    <dt class="tio-text-sm-normal tio-fg-secondary">
                      {{t "tuition_assistance.applications.manage.rail.final_approved"}}
                    </dt>
                    <dd class="tio-text-sm-normal tio-fg-primary">{{this.finalApprovedLabel}}</dd>
                  </div>
                {{/if}}
              </dl>
            </div>

            {{! Disbursement }}
            {{#if this.disbursementCards.length}}
              <div class="border-t tio-border-default"></div>
              <div data-test-manage-disbursement>
                <h2 class="tio-text-base-semibold tio-fg-primary">
                  {{t "tuition_assistance.applications.manage.rail.disbursement"}}
                </h2>
                <div class="mt-3">
                  <DisbursementSection @cards={{this.disbursementCards}} />
                </div>
              </div>
            {{/if}}
          </section>

          {{! Latest Comment — separate card }}
          {{#if this.latestComment}}
            <section class="tio-bg-surface rounded-2xl border tio-border-default p-5">
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.latest_comment"}}
              </h2>
              <p class="tio-text-sm-normal tio-fg-primary mt-3 line-clamp-3">
                {{this.latestComment.text}}
              </p>
              {{#if this.latestComment.date}}
                <p class="tio-text-xs-normal tio-fg-secondary mt-1">{{this.latestComment.date}}</p>
              {{/if}}
            </section>
          {{/if}}
        </aside>
      </div>

      <ActionToolbar
        @application={{this.application}}
        @activeDrawer={{this.activeToolbarDrawer}}
        @onDrawerOpen={{this.handleToolbarDrawerOpen}}
        @unreadCommentCount={{this.unreadCommentCount}}
      />

      {{#if this.programTemplate}}
        <ShowCourseDrawer
          @isOpen={{this.isCourseDrawerOpen}}
          @onClose={{this.closeCourseDrawer}}
          @course={{this.courseShowing}}
          @programTemplate={{this.programTemplate}}
          @header={{this.courseDrawerHeader}}
        />
      {{/if}}

      <Drawer
        @isOpen={{this.isActivityDrawerOpen}}
        @onClose={{this.closeActivityDrawer}}
        @size="xl"
      >
        <div class="flex-none border-b tio-border-default px-6 pt-4 pb-3">
          <h2 class="tio-text-lg-semibold tio-fg-primary">
            {{t
              "tuition_assistance.course_cash.card_drawer_title"
              courseCash=(t "common.tas.course_cash.default" htmlSafe=true)
              htmlSafe=true
            }}
          </h2>
        </div>

        <div class="flex-none border-b tio-border-default px-6" data-test-card-drawer-tabs>
          <div
            class="tio-row-loose"
            role="tablist"
            aria-label={{t "tuition_assistance.course_cash.card_drawer_tablist_label"}}
          >
            <Button
              @appearance="minimal"
              class="py-3 rounded-none tio-text-sm-semibold uppercase tracking-wide transition-colors
                {{if
                  (eq this.activeCardTab 'details')
                  'tio-fg-brand border-b-2 tio-border-brand'
                  'tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.switchCardTab "details")}}
              {{on "keydown" this.handleCardTabKeydown}}
              {{this.registerCardTabButton "details"}}
              role="tab"
              aria-selected={{if (eq this.activeCardTab "details") "true" "false"}}
              aria-controls="manage-card-drawer-panel"
              id="manage-card-details-tab-button"
              tabindex={{if (eq this.activeCardTab "details") "0" "-1"}}
              data-test-card-details-tab
            >
              {{t "tuition_assistance.course_cash.card_details_tab"}}
            </Button>
            <Button
              @appearance="minimal"
              class="py-3 rounded-none tio-text-sm-semibold uppercase tracking-wide transition-colors
                {{if
                  (eq this.activeCardTab 'activity')
                  'tio-fg-brand border-b-2 tio-border-brand'
                  'tio-fg-muted hover:tio-fg-secondary'
                }}"
              {{on "click" (fn this.switchCardTab "activity")}}
              {{on "keydown" this.handleCardTabKeydown}}
              {{this.registerCardTabButton "activity"}}
              role="tab"
              aria-selected={{if (eq this.activeCardTab "activity") "true" "false"}}
              aria-controls="manage-card-drawer-panel"
              id="manage-card-activity-tab-button"
              tabindex={{if (eq this.activeCardTab "activity") "0" "-1"}}
              data-test-card-activity-tab
            >
              {{t "tuition_assistance.course_cash.card_activity_tab"}}
            </Button>
          </div>
        </div>

        <div
          id="manage-card-drawer-panel"
          role="tabpanel"
          tabindex="0"
          aria-labelledby={{if
            (eq this.activeCardTab "details")
            "manage-card-details-tab-button"
            "manage-card-activity-tab-button"
          }}
        >
          {{#if (eq this.activeCardTab "details")}}
            <div class="h-full overflow-y-auto">
              <CardDetailsTab @application={{this.application}} />
            </div>
          {{else if this.pcsCards.length}}
            <div class="h-full overflow-y-auto">
              {{#each this.pcsCardsWithMeta key="card.id" as |item|}}
                <ActivityView
                  @card={{item.card}}
                  @cardNumber={{item.cardNumber}}
                  @totalCards={{item.totalCards}}
                />
              {{/each}}
            </div>
          {{else}}
            <DrawerBody>
              <p class="tio-text-sm-normal tio-fg-secondary py-4 italic">
                {{t "tuition_assistance.course_cash.activity.no_activity"}}
              </p>
            </DrawerBody>
          {{/if}}
        </div>
      </Drawer>

      <Drawer
        @isOpen={{this.isCommentsDrawerOpen}}
        @onClose={{this.closeCommentsDrawer}}
        @size="xl"
      >
        <ManageCommentsDrawer
          @application={{this.application}}
          @authoredByPerson={{this.sessionContext.person}}
          @authoredByUser={{this.sessionContext.user}}
        />
      </Drawer>

      <Drawer
        @isOpen={{this.isTimelineDrawerOpen}}
        @onClose={{this.closeTimelineDrawer}}
        @size="xl"
      >
        <TimelineDrawer @history={{this.application.fullHistory}} />
      </Drawer>

      <Drawer
        @isOpen={{this.isAttachmentsDrawerOpen}}
        @onClose={{this.closeAttachmentsDrawer}}
        @size="xl"
      >
        <ManageAttachmentsDrawer
          @application={{this.application}}
          @allAttachments={{this.allAttachments}}
        />
      </Drawer>

      <ReceiptUploadModal
        @isOpen={{this.isReceiptModalOpen}}
        @transaction={{this.selectedReceiptTransaction}}
        @onClose={{this.closeReceiptModal}}
        @onUpload={{this.handleReceiptUpload}}
        @onRemoveReceipt={{this.handleRemoveReceipt}}
      />
    </section>
  </template>
}
